import { FC } from 'react';
import { X, BookOpen } from 'lucide-react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { useGetCourseQuery } from '@teachur/http-client';
import { Loader } from '@new/common/ui/Loader/Loader';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';
import { CourseAims } from '@new/features/courses/components/CourseAims/CourseAims';
import { CourseLessons } from '@new/features/courses/components/CourseLessons/CourseLessons';
import { stripHtml } from '../../utils/projectReviewFormatters';

interface CourseDetailsSidePanelProps {
  courseId: number;
  onClose: () => void;
}

export const CourseDetailsSidePanel: FC<CourseDetailsSidePanelProps> = ({ courseId, onClose }) => {
  const { data: course, isLoading } = useGetCourseQuery(courseId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <Loader />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex flex-col h-full p-4">
        <EmptyState
          message="Course not found"
          description="The course details could not be loaded."
        />
      </div>
    );
  }

  const description = stripHtml(course.shortDescription || course.longDescription || '').trim();

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-(--neutral-white-NW800) mr-[2%]">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-(--neutral-black-NB500)" />
          <h3 className="[font-size:var(--fs-h6)]! font-bold text-(--neutral-black-NB1000)! mb-0!">
            Course details
          </h3>
        </div>
        <button
          onClick={onClose}
          className="rounded-md text-(--neutral-black-NB500) hover:text-(--neutral-black-NB900) cursor-pointer"
          aria-label="Close course details"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <ScrollArea.Root className="flex-1 min-h-0 overflow-hidden">
        <ScrollArea.Viewport className="w-full h-full p-4 mr-[2%]">
          <div className="flex flex-col gap-6">
            <div>
              <h4 className="[font-size:var(--fs-h5)]! font-bold text-(--neutral-black-NB1000)! m-0! mb-2">
                {course.name}
              </h4>
              {description && (
                <p className="m-0! [font-size:var(--fs-p5)]! text-(--neutral-black-NB700)! whitespace-normal wrap-break-word">
                  {description}
                </p>
              )}
            </div>

            {course.goals && course.goals.length > 0 && <CourseAims goals={course.goals} />}

            <CourseLessons
              courseId={course.id}
              courseStatus={course.status}
              isPaused={course.isPaused}
              disableNavigation
              titleClassName="[font-size:var(--fs-h6)]! text-(--neutral-black-NB800)!"
            />
          </div>
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar
          orientation="vertical"
          className="flex w-2.5 bg-transparent p-0.5 hover:bg-gray-100 transition-colors mr-1"
        >
          <ScrollArea.Thumb className="flex-1 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors" />
        </ScrollArea.Scrollbar>
        <ScrollArea.Corner className="bg-gray-100" />
      </ScrollArea.Root>
    </div>
  );
};
